import { useDispatch, useSelector } from "react-redux";
import ProductUnitService from "../services/ProductUnitService";
import useAlerts from "./useAlerts";
import {
  loadingProductUnits,
  loadingProductUnitsDropdown,
  deactivateStateProductUnit,
  updateProductUnit,
  addProductUnit,
} from "../store/slices/productUnit/productUnitSlice";
import type { RootState, AppDispatch } from "../store/store";
import useLoading from "./useLoading";
import { IProductUnitCreate } from "../types/productUnit/productUnitInterfaces";
import useApiErrorHandler from "./useApiErrorHandler";
import useModals from "./useModals";

export const useProductUnits = () => {
  const { show: showAlert } = useAlerts();
  const { openModal } = useModals();
  const { handle } = useApiErrorHandler();
  const dispatch: AppDispatch = useDispatch();
  const { showLoadingIndicator, hideLoadingIndicator } = useLoading();

  // Estados del store
  const { productUnits, productUnitsDropdown, productUnitSelected } = useSelector(
    (state: RootState) => state.productUnit
  );

  const handlerSearchByType = async ({ searchType, name }) => {
    try {
      showLoadingIndicator(); // Mostrar el modal de carga

      let result;
      switch (searchType) {
        case 'all':
          result = await ProductUnitService.getAll();
          dispatch(loadingProductUnits(result)); // Cargar directamente
          break;
        case 'name':
          result = await ProductUnitService.findByName(name);

          dispatch(loadingProductUnits(result?.data || [])); // Si `data` no existe, enviamos un array vacío
          break;
        default:
          throw new Error(`Tipo de búsqueda no válido: ${searchType}`);
      }

      // console.log("Resultado de la búsqueda:", result);

    } catch (error: any) {
      handle(error);
    } finally {
      hideLoadingIndicator(); // Ocultar el modal de carga al finalizar la solicitud
    }
  };

  /**
   * Obtener todas las unidades de medida.
   */
  const fetchProductUnits = async () => {
    try {
      showLoadingIndicator();
      const response = await ProductUnitService.getAll();
      dispatch(loadingProductUnits(response));
    } catch (error) {
      handle(error);
    } finally {
      hideLoadingIndicator();
    }
  };


  /**
   * Obtener unidades de medida para el dropdown.
   */
  const fetchProductUnitsDropdown = async () => {
    try {
      const response = await ProductUnitService.getDropdown();
      dispatch(loadingProductUnitsDropdown(response));
    } catch (error) {
      handle(error);
    }
  };

  const handlerAddProductUnit = async (data: IProductUnitCreate) => {
    console.log(data);
    try {
      if (data.id === 0) {
        const { id, ...unitData } = data;
        const response = await ProductUnitService.save(unitData);
        const { statusCode, title, message, type, data: newUnit } = response;

        if (statusCode === 200) {
          dispatch(addProductUnit(newUnit));
          openModal(type, { title, message, });
        } else {
          showAlert('error', 'Error', 'No se pudo guardar la unidad de medida');
        }
      } else {
        const response = await ProductUnitService.update(data.id, data);
        const { statusCode, type, title, message, data: updatedUnit } = response;

        if (statusCode === 200) {
          dispatch(updateProductUnit(updatedUnit));
          openModal(type, { title, message, });
        } else {
          showAlert('error', 'Error', 'No se pudo actualizar la unidad de medida');
        }
      }
    } catch (error: any) {
      handle(error);
    }
  };

  const toggleProductUnitStatus = async (id: number) => {
    try {
      const response = await ProductUnitService.deactivate(id);

      // ✅ Asegurar que response tenga datos antes de acceder a ellos
      const title = response?.title ?? "Operación completada";
      const message = response?.message ?? "El estado de la unidad de medida ha sido actualizado.";
      const type = response?.type ?? "success";

      showAlert(type, title, message);

      // ✅ Asegurar que response.data es válido antes de actualizar el estado
      dispatch(deactivateStateProductUnit(response?.data ?? {}));
    } catch (error: any) {
      handle(error);
    }
  };


  return {
    handlerSearchByType,
    productUnits,
    productUnitsDropdown,
    productUnitSelected,
    handlerAddProductUnit,
    fetchProductUnits,
    fetchProductUnitsDropdown,
    toggleProductUnitStatus,
  };
};
